import { ArrowUpRight, MousePointerClick } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { longDate } from "../format";
import type { ChartStat } from "../types";

const COLORS = ["var(--c1)", "var(--c2)", "var(--c3)", "var(--c4)", "var(--c5)", "var(--c6)"];
const W = 760, H = 300, PAD = { l: 38, r: 14, t: 14, b: 28 };
const GAP_DAYS = 150; // a longer silence than this breaks the line instead of drawing a false trend

const day = (iso: string) => Date.parse(`${iso}T00:00:00Z`) / 86_400_000;

interface Point { id: string; slug: string; color: string; date: string; position: number; x: number; y: number }

/** Position over time, one line per chart, #1 at the top. Hover a dot for its place, click it to open that chart. */
export function PositionChart({ history, charts, trackId }: {
  history: Record<string, { date: string; position: number }[]>;
  charts: ChartStat[];
  trackId: number;
}) {
  const navigate = useNavigate();
  const svg = useRef<SVGSVGElement>(null);
  const armed = useRef<string | null>(null); // touch: the first tap shows the tooltip, the second opens the chart
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [hover, setHover] = useState<Point | null>(null);

  const model = useMemo(() => {
    const series = Object.entries(history).filter(([, pts]) => pts.length);
    const all = series.flatMap(([, pts]) => pts);
    if (!all.length) return null;
    let t0 = Math.min(...all.map((p) => day(p.date))), t1 = Math.max(...all.map((p) => day(p.date)));
    if (t1 - t0 < 30) { t0 -= 15; t1 += 15; }
    const maxPos = Math.max(10, Math.ceil(Math.max(...all.map((p) => p.position)) / 10) * 10);
    const x = (iso: string) => PAD.l + ((day(iso) - t0) / (t1 - t0)) * (W - PAD.l - PAD.r);
    const y = (pos: number) => PAD.t + ((pos - 1) / (maxPos - 1)) * (H - PAD.t - PAD.b);
    const years: number[] = [];
    const y0 = new Date(t0 * 86_400_000).getUTCFullYear(), y1 = new Date(t1 * 86_400_000).getUTCFullYear();
    const step = Math.max(1, Math.ceil((y1 - y0 + 1) / 8));
    for (let yr = y0 + 1; yr <= y1; yr += step) years.push(yr);
    const yTicks = [1, ...Array.from({ length: maxPos / 10 }, (_, i) => (i + 1) * 10)];
    const points: Point[] = series.flatMap(([slug, pts], si) =>
      pts.map((p) => ({ id: `${slug}|${p.date}|${p.position}`, slug, color: COLORS[si % COLORS.length]!, date: p.date, position: p.position, x: x(p.date), y: y(p.position) })));
    return { series, x, y, years, yTicks, points };
  }, [history]);

  if (!model) return null;
  const name = (slug: string) => charts.find((c) => c.chart === slug)?.chartName ?? slug;
  const visible = model.points.filter((p) => !hidden.has(p.slug));

  const open = (p: Point) => navigate(`/listy/${p.slug}/${p.date}?utwor=${trackId}`);

  /** The dot closest to the pointer, if one is close enough to mean it. */
  const nearest = (clientX: number, clientY: number, touch: boolean): Point | null => {
    const el = svg.current;
    if (!el) return null;
    const box = el.getBoundingClientRect();
    const scale = W / box.width;
    const mx = (clientX - box.left) * scale, my = (clientY - box.top) * scale;
    const reach = (touch ? 26 : 15) * scale;
    let best: Point | null = null, bestD = reach * reach;
    for (const p of visible) {
      const d = (p.x - mx) ** 2 + (p.y - my) ** 2;
      if (d <= bestD) { best = p; bestD = d; }
    }
    return best;
  };

  const toggle = (slug: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else if (next.size < model.series.length - 1) next.add(slug); // never hide everything
      return next;
    });

  const left = hover ? (hover.x / W) * 100 : 0;
  const top = hover ? (hover.y / H) * 100 : 0;
  const shiftX = left < 22 ? "-14%" : left > 78 ? "-86%" : "-50%";
  const below = top < 38;

  return (
    <figure className="poschart">
      <div className="poschart__plot" style={{ position: "relative" }}>
        <svg
          ref={svg}
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label="Wykres pozycji utworu w czasie. Poniżej znajdziesz te same notowania w formie listy."
          className={hover ? "is-hot" : undefined}
          onPointerMove={(e) => { if (e.pointerType !== "touch") setHover(nearest(e.clientX, e.clientY, false)); }}
          onPointerLeave={(e) => { if (e.pointerType !== "touch") setHover(null); }}
          onClick={(e) => {
            const touch = (e.nativeEvent as PointerEvent).pointerType === "touch";
            const p = nearest(e.clientX, e.clientY, touch);
            if (!p) { armed.current = null; setHover(null); return; }
            if (!touch || armed.current === p.id) open(p);
            else { armed.current = p.id; setHover(p); }
          }}
        >
          {model.yTicks.map((p) => (
            <g key={p}>
              <line x1={PAD.l} x2={W - PAD.r} y1={model.y(p)} y2={model.y(p)} className="grid" />
              <text x={PAD.l - 8} y={model.y(p) + 4} textAnchor="end" className="tick">{p}</text>
            </g>
          ))}
          {model.years.map((yr) => {
            const x = model.x(`${yr}-01-01`);
            return (
              <g key={yr}>
                <line x1={x} x2={x} y1={PAD.t} y2={H - PAD.b} className="grid grid--v" />
                <text x={x} y={H - 8} textAnchor="middle" className="tick">{yr}</text>
              </g>
            );
          })}
          {model.series.map(([slug, pts], si) => {
            if (hidden.has(slug)) return null;
            const color = COLORS[si % COLORS.length];
            const segments: typeof pts[] = [];
            pts.forEach((p, i) => {
              const prev = pts[i - 1];
              if (!prev || day(p.date) - day(prev.date) > GAP_DAYS) segments.push([p]);
              else segments[segments.length - 1]?.push(p);
            });
            return (
              <g key={slug} style={{ color }} pointerEvents="none">
                {segments.map((seg, i) =>
                  seg.length > 1 ? (
                    <polyline key={i} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" opacity="0.85"
                      points={seg.map((p) => `${model.x(p.date).toFixed(1)},${model.y(p.position).toFixed(1)}`).join(" ")} />
                  ) : null,
                )}
                {pts.map((p) => (
                  <circle key={p.date + p.position} cx={model.x(p.date)} cy={model.y(p.position)} r={pts.length > 80 ? 2.2 : 3.4} fill="currentColor" />
                ))}
              </g>
            );
          })}
          {hover && (
            <g pointerEvents="none">
              <line x1={hover.x} x2={hover.x} y1={PAD.t} y2={H - PAD.b} className="guide" />
              <circle cx={hover.x} cy={hover.y} r={7} fill={hover.color} className="ring" />
            </g>
          )}
        </svg>
        {hover && (
          <div
            className="tip"
            role="status"
            style={{ left: `${left}%`, top: `${top}%`, transform: `translate(${shiftX}, ${below ? "16px" : "calc(-100% - 16px)"})` }}
          >
            <div className="tip__chart"><i style={{ background: hover.color }} />{name(hover.slug)}</div>
            <div className="tip__pos">Miejsce {hover.position}</div>
            <div className="tip__date">{longDate(hover.date)}</div>
            <div className="tip__go">Otwórz to notowanie <ArrowUpRight size={14} aria-hidden="true" /></div>
          </div>
        )}
      </div>
      <figcaption>
        <div className="legend">
          {model.series.map(([slug], si) => (
            <button key={slug} type="button" aria-pressed={!hidden.has(slug)} onClick={() => toggle(slug)} title="Pokaż lub ukryj tę listę">
              <i style={{ background: COLORS[si % COLORS.length] }} />{name(slug)}
            </button>
          ))}
        </div>
        <p className="poschart__hint">
          <MousePointerClick size={15} aria-hidden="true" />
          Najedź na punkt, żeby zobaczyć miejsce, kliknij, żeby otworzyć to notowanie.
        </p>
      </figcaption>
    </figure>
  );
}
